<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { NSpace, NButton, NSwitch, NText, NList, NListItem, NScrollbar } from 'naive-ui';
import { formatElapsedTime } from '@awesomeclocks/utils';
import type { StopwatchWidgetConfig } from '@awesomeclocks/types';
import WidgetWrapper from './WidgetWrapper.vue';

interface Props {
  widget: StopwatchWidgetConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  remove: [];
  updateName: [name: string];
  updateSettings: [settings: StopwatchWidgetConfig['settings']];
  updateState: [state: StopwatchWidgetConfig['state']];
}>();

const elapsed = ref(props.widget.state?.elapsed ?? 0);
const isRunning = ref(props.widget.state?.isRunning ?? false);
const laps = ref<number[]>(props.widget.state?.laps ?? []);
let intervalId: number | null = null;
let startTime = 0;
let pausedTime = 0;
let lastEmitAt = 0;

const displayTime = computed(() =>
  formatElapsedTime(elapsed.value, props.widget.settings.showMilliseconds)
);

const start = () => {
  if (!isRunning.value) {
    isRunning.value = true;
    startTime = Date.now() - pausedTime;
    emitState();
    startInterval();
  }
};

const pause = () => {
  if (isRunning.value) {
    isRunning.value = false;
    pausedTime = Date.now() - startTime;
    stopInterval();
    emitState();
  }
};

const reset = () => {
  stopInterval();
  isRunning.value = false;
  elapsed.value = 0;
  pausedTime = 0;
  laps.value = [];
  emitState();
};

const lap = () => {
  if (isRunning.value && props.widget.settings.lapRecording) {
    laps.value.push(elapsed.value);
    emitState();
  }
};

const startInterval = () => {
  if (intervalId !== null) return;

  const interval = props.widget.settings.showMilliseconds ? 10 : 100;

  lastEmitAt = 0;
  intervalId = window.setInterval(() => {
    elapsed.value = Date.now() - startTime;
    if (Date.now() - lastEmitAt >= 1000) {
      lastEmitAt = Date.now();
      emitState();
    }
  }, interval);
};

const stopInterval = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const emitState = () => {
  emit('updateState', {
    elapsed: elapsed.value,
    isRunning: isRunning.value,
    laps: laps.value,
  });
};

const updateShowMilliseconds = (showMilliseconds: boolean) => {
  emit('updateSettings', { ...props.widget.settings, showMilliseconds });
  if (isRunning.value) {
    stopInterval();
    startInterval();
  }
};

const updateLapRecording = (lapRecording: boolean) => {
  emit('updateSettings', { ...props.widget.settings, lapRecording });
};

const getLapDiff = (index: number): number => {
  if (index === 0) {
    return laps.value[0] ?? 0;
  }
  const current = laps.value[index] ?? 0;
  const previous = laps.value[index - 1] ?? 0;
  return current - previous;
};

onUnmounted(() => {
  stopInterval();
});

onMounted(() => {
  if (isRunning.value) {
    startTime = Date.now() - elapsed.value;
    startInterval();
  }
});
</script>

<template>
  <WidgetWrapper
    :widget="widget"
    @remove="emit('remove')"
    @update-name="emit('updateName', $event)"
  >
    <NSpace vertical align="center" :size="20" style="width: 100%">
      <!-- Stopwatch Display -->
      <div class="stopwatch-display">
        <NText class="time-text" :depth="1">{{ displayTime }}</NText>
      </div>

      <!-- Controls -->
      <NSpace>
        <NButton v-if="!isRunning" type="primary" size="large" @click="start">
          Start
        </NButton>
        <NButton v-else size="large" @click="pause"> Pause </NButton>
        <NButton
          v-if="widget.settings.lapRecording && isRunning"
          size="large"
          type="info"
          @click="lap"
        >
          Lap
        </NButton>
        <NButton size="large" :disabled="isRunning" @click="reset"> Reset </NButton>
      </NSpace>

      <!-- Laps -->
      <div v-if="widget.settings.lapRecording && laps.length > 0" class="laps-container">
        <NText strong>Laps:</NText>
        <NScrollbar style="max-height: 150px">
          <NList>
            <NListItem v-for="(lap, index) in [...laps].reverse()" :key="index">
              <NSpace justify="space-between" style="width: 100%">
                <NText>Lap {{ laps.length - index }}</NText>
                <NSpace :size="16">
                  <NText :depth="2"
                    >+{{
                      formatElapsedTime(
                        getLapDiff(laps.length - index - 1),
                        widget.settings.showMilliseconds
                      )
                    }}</NText
                  >
                  <NText>{{
                    formatElapsedTime(lap, widget.settings.showMilliseconds)
                  }}</NText>
                </NSpace>
              </NSpace>
            </NListItem>
          </NList>
        </NScrollbar>
      </div>

      <!-- Settings -->
      <NSpace vertical :size="8" class="settings">
        <NSpace align="center" justify="space-between">
          <NText>Show Milliseconds:</NText>
          <NSwitch
            :value="widget.settings.showMilliseconds"
            @update:value="updateShowMilliseconds"
          />
        </NSpace>

        <NSpace align="center" justify="space-between">
          <NText>Lap Recording:</NText>
          <NSwitch
            :value="widget.settings.lapRecording"
            @update:value="updateLapRecording"
          />
        </NSpace>
      </NSpace>
    </NSpace>
  </WidgetWrapper>
</template>

<style scoped>
.stopwatch-display {
  padding: 32px;
}

.time-text {
  font-size: 3rem;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.05em;
}

.laps-container {
  width: 100%;
  max-width: 400px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.settings {
  width: 100%;
  max-width: 300px;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
